/**
 * Estimate summary.
 *
 * 1a is the base. The hourly profile borrows 1b's instrument-panel density —
 * that is the one place the dashboard direction genuinely reads better, and
 * it arrives as ONE COMPONENT rather than as a second theme.
 *
 * THE COLLAPSED PEAK. The engine defines three bounds: low (everything at
 * maximum running draw), expected (plus the largest motor start), high
 * (everything starting at once). No equipment entry carries inrush data, so
 * all three are identical and the engine emits ONE figure rather than a band
 * nobody computed. `peak_band_resolved: false` says so, and the collapse is
 * shown as a CONSEQUENCE OF MISSING DATA, not as a design choice.
 *
 * MISSING HOURLY DATA IS NOT A FAILED ESTIMATE. The chart renders its own
 * unavailable state and the figures above it stand.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  band, hasSpread, modeller,
  type Estimate, type EstimateBand, type EstimateHour,
} from '../api';
import { PageHeader } from '../shell/Shell';
import { Empty, ErrorState, Loading } from '../shell/states';
import { Figure, Note, Tag } from '../shell/provenance';
import './estimate.css';

/**
 * One headline output.
 *
 * "COLLAPSED" IS A CLAIM AND IS ONLY MADE WITH A REASON. Three equal bounds
 * are not self-explanatory: peak collapses because no inrush data exists to
 * separate the bounds, which is a finding worth labelling. Anything else with
 * equal bounds simply has no band, and printing `104 – 104` or tagging it
 * "Collapsed" with nothing to read would be a state nobody can act on — the
 * failure the brief describes as a refusal rendered as an error.
 */
function Output(
  { label, unit, band, collapsedWhy }:
  {
    label: string;
    unit: string;
    band: EstimateBand;
    /** Supplying this asserts the collapse AND explains it. Omit it and a
     *  single-valued output just renders as one figure. */
    collapsedWhy?: string;
  },
) {
  /* A band needs BOTH bounds. `peak_kw_low` and `_high` come back NULL when
     the bounds cannot be computed, and null is not zero and not equal to
     expected — rendering the band branch on nulls printed an empty "– low /
     high" with no numbers in it. */
  const spread = hasSpread(band);
  return (
    <div className="pem-out">
      <div className="pem-out__head">
        <span className="pem-label">{label}</span>
        {!spread && collapsedWhy && <Tag tone="caution">Collapsed</Tag>}
      </div>
      <Figure value={band.expected} unit={unit} />
      {spread ? (
        <div className="pem-out__band">
          <span>{band.low}</span>
          <span className="pem-out__band-sep">–</span>
          <span>{band.high}</span>
          <span className="pem-provenance">low / high</span>
        </div>
      ) : (
        collapsedWhy && <Note>{collapsedWhy}</Note>
      )}
    </div>
  );
}

/** kVA is refused whenever any line lacks a power factor. A refusal occupies
 *  the place an answer would occupy, names what is missing and who supplies
 *  it — it is not an error and is not styled as one. */
function RefusedOutput({ label, why, who }: { label: string; why: string; who: string }) {
  return (
    <div className="pem-out pem-out--refused">
      <div className="pem-out__head">
        <span className="pem-label">{label}</span>
        <Tag tone="caution">Refused</Tag>
      </div>
      <p className="pem-out__why">{why}</p>
      <Note>{who}</Note>
    </div>
  );
}

/**
 * The hourly profile. Denser than the rest of 1a on purpose — this is the
 * component 1b was kept as a reference for.
 *
 * THE BAND IS DRAWN, not just the expected value. The table stores kw_low,
 * kw_expected and kw_high per bucket; showing only the middle one would throw
 * away half of what was computed. Expected is solid, the low-to-high range is
 * the lighter column behind it.
 *
 * THE AXIS FOLLOWS THE MODEL. `local_hour` is NULL on a relative estimate —
 * no clock anchor was supplied — so the axis reads H+0, H+1 … rather than
 * printing 00, 01 and implying a midnight start nobody claimed. The chart
 * must not look more precise about time than the estimate is.
 */
function HourlyProfile({ hours }: { hours: EstimateHour[] }) {
  const ceiling = Math.max(...hours.map((h) => h.kw_high), 1);
  const clocked = hours.some((h) => h.local_hour != null);
  return (
    <div className="pem-hourly">
      <div className="pem-hourly__bars" role="img"
           aria-label={
             `Hourly load profile, ${hours.length} buckets, ` +
             `expected peak ${Math.max(...hours.map((h) => h.kw_expected))} kW`
           }>
        {hours.map((h) => (
          <div key={h.hour_index} className="pem-hourly__col">
            <span className="pem-hourly__kw">{h.kw_expected}</span>
            <div className="pem-hourly__plot">
              {/* The uncertainty band: bottom edge at kw_low, top at kw_high,
                  so its HEIGHT is the spread rather than the magnitude. A band
                  drawn from the axis would read as "up to high" and lose the
                  lower bound entirely. */}
              <div className="pem-hourly__range"
                   style={{
                     bottom: `${(h.kw_low / ceiling) * 100}%`,
                     height: `${((h.kw_high - h.kw_low) / ceiling) * 100}%`,
                   }} />
              {/* Expected, from the axis. The number a reader takes away. */}
              <div className="pem-hourly__bar"
                   style={{ height: `${(h.kw_expected / ceiling) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="pem-hourly__axis">
        {hours.map((h) => (
          <span key={h.hour_index}>
            {h.local_hour != null
              ? String(h.local_hour).padStart(2, '0')
              /* Elapsed, signed, unwrapped — the stored coordinate itself. */
              : `H${h.hour_index >= 0 ? '+' : ''}${h.hour_index}`}
          </span>
        ))}
      </div>
      <Note>
        Stepped from each line's start offset, duration and duty cycle — not a
        curve fitted to the daily total.
        {clocked
          ? ' Axis is local clock hour.'
          : ' No clock anchor on this estimate: the axis is elapsed hours from' +
            ' the model origin, not a time of day.'}
      </Note>
    </div>
  );
}

type S = { t: 'loading' } | { t: 'error'; e: unknown } | { t: 'ok'; est: Estimate };

export function EstimateSummary() {
  const { slug = '', id = '' } = useParams();
  const [state, setState] = useState<S>({ t: 'loading' });

  useEffect(() => {
    let live = true;
    /* ONE request. `hourly` arrives with the estimate; fetching it separately
       asked the same URL twice for one screen. */
    setState({ t: 'loading' });
    modeller.estimate(Number(id))
      .then((est) => { if (live) setState({ t: 'ok', est }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, [id]);

  if (state.t === 'loading') return <><PageHeader title="Estimate" /><Loading what="the estimate" /></>;
  if (state.t === 'error') return <><PageHeader title="Estimate" /><ErrorState what="the estimate" error={state.e} /></>;

  const { est } = state;
  const hours: EstimateHour[] = est.hourly ?? [];
  return (
    <>
      <PageHeader
        title="Estimate"
        count={est.status}
        actions={<Link className="pem-link" to={`/modeller/productions/${slug}`}>← {slug}</Link>}
      />

      <div className="pem-est__basis">
        <Note>
          Deterministic: equipment × quantity × hours × duty cycle. No LLM
          inputs here.
        </Note>
        <div className="pem-est__profile">
          <span className="pem-label">Profile</span>
          <span className="pem-est__profile-name">
            {est.profile_key ?? 'none'}
            {est.profile_version != null ? ` · v${est.profile_version}` : ''}
          </span>
          <span className="pem-provenance">Method {est.method_version}</span>
        </div>
      </div>

      <div className="pem-outs">
        <Output label="Daily energy" unit="kWh" band={band(est, 'daily_kwh')} />
        <Output label="Sustained" unit="kW" band={band(est, 'sustained_kw')} />
        <Output
          label="Peak"
          unit="kW"
          band={band(est, 'peak_kw')}
          /* Only when the SERVER says the band is unresolved. Equal numbers
             alone are not evidence of the reason. */
          collapsedWhy={est.peak_band_resolved ? undefined :
            'No bounds were computed — no equipment entry carries inrush ' +
            'data, so low and high cannot be separated from expected. The ' +
            'server returns them as absent rather than repeating this ' +
            'figure three times. The band returns once nameplate data exists.'
          }
        />
        <div className="pem-out">
          <div className="pem-out__head"><span className="pem-label">Operating window</span></div>
          <Figure
            value={
              est.call_time_local && est.estimated_wrap_local
                ? `${est.call_time_local.slice(0, 5)}–${est.estimated_wrap_local.slice(0, 5)}`
                : '—'
            }
          />
          <Note>
            {est.day_length_h != null ? `${est.day_length_h} h · ` : ''}
            anchored to first activity on site, excluding travel
          </Note>
        </div>
        <RefusedOutput
          label="kVA"
          why="No power factor on one or more lines."
          who="Resolved by Equoia — datasheet"
        />
      </div>

      <section className="pem-est__section">
        <div className="pem-est__section-head">
          <h2>Hourly load profile</h2>
          {hours.length > 0 && <Tag tone="assumed">{hours.length} hours</Tag>}
        </div>
        {/* An estimate with no buckets is a legitimate state — a relative
            estimate that computed no window has none — and is NOT a failed
            estimate. The figures above stand either way. */}
        {hours.length
          ? <HourlyProfile hours={hours} />
          : <Empty
              title="No hourly buckets"
              body={
                'This estimate carries no load profile. The figures above are ' +
                'unaffected — they are computed from the lines, not from the ' +
                'profile.'
              } />}
      </section>

      <div className="pem-detail-foot">
        <Note>
          {est.assumed_item_count} of {est.item_count} lines are assumed.
          Inputs and assumptions are frozen on commit.
        </Note>
      </div>
    </>
  );
}
